/* eslint-disable object-shorthand */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import FileFormat from '../FileFormat';
import PopupBox from '../ContentUploadConfirmation';

function HtmlTemplateTwentySix({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [
    openContentUploadConfirmation,
    setOpenContentUploadConfirmation,
  ] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);
  const cssStyles = `
body {
    margin: 0;
    padding: 0;
}
@font-face {
  font-family: "Urbanist";
  src: url(../../../assets/fonts/urbanist/Urbanist-Bold.ttf) format("truetype");
  font-weight: 700;
}

@font-face {
  font-family: "Cabin";
  src: url(../../../assets/fonts/cabin/Cabin-Italic.ttf)
    format("truetype");
  font-weight: 700;
}

@font-face {
  font-family: "Atteron";
  src: url(../../../assets/fonts/Atteron/Atteron.ttf)
    format("truetype");
  font-weight: 400;
}

@font-face {
  font-family: "Great Vibes";
  src: url(../../../assets/fonts/greatVibes/GreatVibes-Regular.ttf)
    format("truetype");
  font-weight: 500;
}

.template-container-26 {
 width: 100%;
 height: 100vh;
 box-sizing: border-box;
 padding: 5px;
}
.coming-soon {
 text-align: center;
 color: #6b3f23;
 padding: 20px;
 max-width: 700px;
}

.coming-soon .title {
 font-size: clamp(2rem, 5vw, 3.5rem);
 letter-spacing: 2px;
 font-weight: 400;
 margin-bottom: 20px;
}

.coming-soon .image-wrapper {
 position: relative;
 display: inline-block;
 margin: 20px auto;
}

.coming-soon .image-wrapper img {
 width: clamp(250px, 60vw, 400px);
 border-radius: 20px;
 display: block;
}

.coming-soon .image-wrapper .decor {
 position: absolute;
 top: 50%;
 transform: translateY(-50%);
 font-size: 1.2rem;
 color: #6b3f23;
}

.coming-soon .image-wrapper .decor.decor-left {
 left: -30px;
}

.coming-soon .image-wrapper .decor.decor-right {
 right: -30px;
}

.coming-soon .subtitle {
 margin-top: 10px;
 font-weight: bold;
 font-size: 1.1rem;
 letter-spacing: 1px;
}

.coming-soon .website {
 margin-top: 25px;
 font-size: 0.9rem;
 letter-spacing: 2px;
 color: #3c2214; /* darkened version of #6b3f23 */
}

/* 📱 Mobile tweaks */
@media (max-width: 480px) {
 .coming-soon .title {
   font-size: 2rem;
 }

 .coming-soon .image-wrapper .decor {
   font-size: 1rem;
 }

 .coming-soon .image-wrapper .decor.decor-left {
   left: -20px;
 }

 .coming-soon .image-wrapper .decor.decor-right {
   right: -20px;
 }

 .coming-soon .subtitle {
   font-size: 1rem;
 }
}

  `;

  const convertImage = async (url) => {
    await utils
      .convertSignedUrl(url)
      .then((response) => {
        setDisable(false);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertBase64 = async () => {
    try {
      // Convert to base64
      const dataUrl = await toPng(htmlRef.current, { cacheBust: true });
      setBase64(dataUrl); // Set the base64 data
      convertImage(dataUrl); // Your function to handle the base64 data
    } catch (error) {
      dispatch(setErrorNotification(error?.response?.data));
    }
  };

  const convertHtml = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link href="https://fonts.googleapis.com/css2
              ?family=Cabin:wght@400;500;700&display=swap" rel="stylesheet">         
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => setData(res));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data) {
      setTimeout(() => {
        convertBase64();
      }, 1000);
      convertHtml();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: (
        <TemplateContentUpload
          base64={base64}
          setNotification={setNotification}
          fileType={fileType}
          htmlContent={htmlContent}
        />
      ),
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: (
        <FileFormat
          setNotification={setNotification}
          handleFileType={handleFileType}
          setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
          data={data}
        />
      ),
    });
  };
  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;
  const backgroundColor = data?.[0]?.styles?.background;
  const backgroundImage = data?.[0]?.template_backgroundImage_url;
  const styles = data?.[0]?.styles?.details;
  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />
      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: (
            <PopupBox
              setOpenContentUploadConfirmation={
                setOpenContentUploadConfirmation
              }
              setNotification={setNotification}
            />
          ),
        }}
      />
      <div className="header-height" />
      <div
        ref={htmlRef}
        className="template-container-26"
        style={{ backgroundColor: backgroundColor }}
      >
        <div className="main-container">
          <div className="continer26">
            <div className="coming-soon">
              <h1
                className="title"
                style={{
                  color: styles?.[0]?.color,
                  fontSize: styles?.[0]?.fontSize,
                }}
              >
                {contentDetails?.[0]?.header}
              </h1>
              <div className="image-wrapper">
                <img
                  className="decor decor-left"
                  src={backgroundImage?.[0]}
                  alt="flower"
                />
                <img src={contentDetails?.[0]?.image} alt="Jewelry" className="image-container" />
                <img
                  className="decor decor-right"
                  src={backgroundImage?.[0]}
                  alt="flower"
                />
              </div>
              <p className="subtitle">{contentDetails?.[0]?.description}</p>
              <p
                className="website"
                style={{
                  color: styles?.[0]?.color,
                  fontSize: styles?.[2]?.fontSize,
                  fontFamily: styles?.[2]?.fontFamily,
                }}
              >
                {contentDetails?.[0]?.website}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

HtmlTemplateTwentySix.propTypes = {
  templateData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    template_title: PropTypes.string.isRequired,
  }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateTwentySix;
